import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Grid,
  Alert,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  ArrowForward as ArrowIcon,
  FamilyRestroom as FamilyIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { apiCall } from '../utils/api';

const FamilySelector = ({ families, onSelectFamily, onCreateFamily, user }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFamilyData, setNewFamilyData] = useState({
    name: '',
    description: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');

    try {
      const data = await apiCall('/api/families', {
        method: 'POST',
        body: JSON.stringify(newFamilyData),
      });

      onCreateFamily(data);
      setShowCreateForm(false);
      setNewFamilyData({ name: '', description: '' });
    } catch (error) {
      setError(error.message || 'Failed to create family');
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (e) => {
    setNewFamilyData({
      ...newFamilyData,
      [e.target.name]: e.target.value
    });
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setError('');
    setNewFamilyData({ name: '', description: '' });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: 'primary.main' }}>
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h3" component="h1" gutterBottom>
            Welcome, {user.name}!
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Select a family to manage runbooks, or create a new one.
          </Typography>
        </Box>

        {/* Existing Families Grid */}
        {families.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
              Your Families
            </Typography>
            <Grid container spacing={3}>
              {families.map(family => (
                <Grid item xs={12} sm={6} md={4} key={family.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => onSelectFamily(family)}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" component="h3" sx={{ flex: 1, mr: 2 }}>
                          {family.name}
                        </Typography>
                        <Chip
                          label={family.user_role}
                          color={family.user_role === 'admin' ? 'primary' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      
                      {family.description && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ mb: 2, lineHeight: 1.5 }}
                        >
                          {family.description}
                        </Typography>
                      )}
                    </CardContent>
                    
                    <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <GroupIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="body2">
                            {family.members?.length || 0} members
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                          <Typography variant="body2" sx={{ mr: 0.5 }}>
                            Select
                          </Typography>
                          <ArrowIcon fontSize="small" />
                        </Box>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* No Families State */}
        {families.length === 0 && !showCreateForm && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 3, bgcolor: 'primary.light' }}>
              <FamilyIcon sx={{ fontSize: 50 }} />
            </Avatar>
            <Typography variant="h4" component="h3" gutterBottom color="text.secondary">
              No families yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Create your first family to start managing runbooks together.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateForm(true)}
            >
              Create Your First Family
            </Button>
          </Box>
        )}

        {/* Create Family Button (when families exist) */}
        {families.length > 0 && !showCreateForm && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateForm(true)}
            >
              Create New Family
            </Button>
          </Box>
        )}

        {/* Create Family Dialog */}
        <Dialog
          open={showCreateForm}
          onClose={handleCloseForm}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Create New Family
          </DialogTitle>
          <Box component="form" onSubmit={handleCreateFamily}>
            <DialogContent>
              <Stack spacing={3}>
                {error && (
                  <Alert severity="error">
                    {error}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Family Name"
                  name="name"
                  value={newFamilyData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., The Smith Family"
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label="Description (optional)"
                  name="description"
                  value={newFamilyData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of your family group"
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={handleCloseForm} variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isCreating || !newFamilyData.name.trim()}
              >
                {isCreating ? 'Creating...' : 'Create Family'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      </Box>
    </Container>
  );
};

export default FamilySelector;